import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Gift, Tag, Store, Upload, X, Loader2, MapPin, Search, Calendar } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { containsProfanity } from '@/lib/profanity';

const CATS = [
  { key: 'free', labelKey: 'create.free', Icon: Gift, color: 'bg-[#7A9A78]' },
  { key: 'yard_sale', labelKey: 'create.yardSale', Icon: Store, color: 'bg-[#D99E4A]' },
];

const todayStr = () => new Date().toISOString().slice(0, 10);
const addDaysStr = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const isoLocal = (date, time) => {
  // date YYYY-MM-DD, time HH:MM → ISO at local TZ
  return new Date(`${date}T${time}:00`).toISOString();
};

export default function CreatePinDialog({ open, onOpenChange, latlng, onCreated, onLatLngChange, editingPin }) {
  const { t } = useTranslation();
  const editingId = editingPin?.id || null;
  const [category, setCategory] = useState('free');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [address, setAddress] = useState('');
  const [showAddress, setShowAddress] = useState(false);
  const [addressResults, setAddressResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [todayOnly, setTodayOnly] = useState(false);
  const [maxPhotos, setMaxPhotos] = useState(4);
  const [enabledCats, setEnabledCats] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get('/settings').then(r => {
      if (r.data?.max_photos_per_post) setMaxPhotos(r.data.max_photos_per_post);
      if (Array.isArray(r.data?.categories)) setEnabledCats(r.data.categories.map(c => c.key));
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) {
      setCategory('free'); setTitle(''); setDescription('');
      setPhotos([]); setAddress(''); setShowAddress(false); setAddressResults([]);
      setStartDate(todayStr()); setEndDate(todayStr());
      setStartTime('09:00'); setEndTime('17:00'); setTodayOnly(false);
      return;
    }
    if (editingPin) {
      setCategory(editingPin.category);
      setTitle(editingPin.title || '');
      setDescription(editingPin.description || '');
      setAddress(editingPin.address || '');
      setShowAddress(!!editingPin.show_address);
      setPhotos((editingPin.photos || []).filter(p => p !== 'default_yard_sale').map(p => ({ path: p, previewUrl: `${api.defaults.baseURL}/files/${p}` })));
      const s = new Date(editingPin.starts_at);
      const e = new Date(editingPin.ends_at);
      setStartDate(s.toISOString().slice(0, 10));
      setEndDate(e.toISOString().slice(0, 10));
      setStartTime(s.toTimeString().slice(0, 5));
      setEndTime(e.toTimeString().slice(0, 5));
      setTodayOnly(false);
    }
  }, [open, editingPin]);

  // Reverse-geocode whenever the pin position changes (drag, address-pick, map-click)
  useEffect(() => {
    if (!open || !latlng) return;
    let cancelled = false;
    api.get('/reverse-geocode', { params: { lat: latlng.lat, lng: latlng.lng } })
      .then(r => { if (!cancelled && r.data?.label) setAddress(r.data.label); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latlng?.lat, latlng?.lng, open]);

  const searchAddress = async () => {
    if (address.trim().length < 3) return;
    setSearching(true);
    try {
      const r = await api.get('/geocode', { params: { q: address } });
      setAddressResults(r.data || []);
    } finally {
      setSearching(false);
    }
  };

  const pickAddress = (r) => {
    setAddress(r.label);
    setAddressResults([]);
    onLatLngChange && onLatLngChange({ lat: r.lat, lng: r.lng });
  };

  const uploadFile = async (file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data.path;
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files).slice(0, maxPhotos - photos.length)) {
        const previewUrl = URL.createObjectURL(file);
        const path = await uploadFile(file);
        setPhotos(prev => [...prev, { path, previewUrl }]);
      }
    } catch (e) { toast.error(e.response?.data?.detail || t('create.uploadFailed')); }
    finally { setUploading(false); }
  };

  const { startsAt, endsAt } = useMemo(() => {
    const sDate = todayOnly ? todayStr() : startDate;
    const eDate = todayOnly ? todayStr() : endDate;
    const sTime = category === 'yard_sale' ? startTime : '00:00';
    const eTime = category === 'yard_sale' ? endTime : '23:59';
    return { startsAt: isoLocal(sDate, sTime), endsAt: isoLocal(eDate, eTime) };
  }, [todayOnly, startDate, endDate, startTime, endTime, category]);

  const titleProfane = useMemo(() => containsProfanity(title), [title]);
  const descProfane = useMemo(() => containsProfanity(description), [description]);

  const submit = async () => {
    if (!title.trim()) { toast.error(t('create.addTitle')); return; }
    if (titleProfane || descProfane) {
      toast.error(t('create.profanityError'));
      return;
    }
    if (!latlng) { toast.error(t('create.pickSpot')); return; }
    if (category === 'free' && photos.length === 0) {
      toast.error(t('create.photoRequired'));
      return;
    }
    const finalPhotos = photos.map(p => p.path);
    if (category === 'yard_sale' && finalPhotos.length === 0) {
      finalPhotos.push('default_yard_sale');
    }
    setSubmitting(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        category,
        price: null,
        lat: latlng.lat,
        lng: latlng.lng,
        address: address.trim(),
        show_address: showAddress,
        photos: finalPhotos,
        starts_at: startsAt,
        ends_at: endsAt,
      };
      if (editingId) {
        await api.patch(`/pins/${editingId}`, body);
      } else {
        await api.post('/pins', body);
      }
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('create.couldNotCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="create-pin-dialog"
          initial={{ x: -460, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -460, opacity: 0 }}
          transition={{ type: 'spring', damping: 28, stiffness: 240 }}
          className="absolute top-32 sm:top-36 left-4 sm:left-6 z-[800] w-[calc(100%-2rem)] sm:w-[420px] glass-panel rounded-3xl overflow-hidden max-h-[calc(100dvh-10rem)] flex flex-col"
          style={{ touchAction: 'pan-y' }}
        >
          <div className="p-5 sm:p-6 border-b border-black/5 flex items-center justify-between">
            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight text-brand-ink">{editingId ? t('create.editTitle') : t('create.createTitle')}</h2>
              <p className="text-xs text-brand-slate mt-0.5">{editingId ? t('create.editSubtitle') : t('create.createSubtitle')}</p>
            </div>
            <button data-testid="cancel-pin-btn" onClick={() => onOpenChange(false)} className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-5 sm:p-6 space-y-4 overflow-y-auto overflow-x-hidden scroll-soft" style={{ touchAction: 'pan-y', overscrollBehavior: 'contain' }}>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">{t('create.category')}</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {CATS.filter(c => !enabledCats || enabledCats.includes(c.key)).map(({ key, labelKey, Icon, color }) => {
                  const active = category === key;
                  return (
                    <button key={key} type="button" data-testid={`category-${key}-btn`} onClick={() => setCategory(key)}
                      className={`rounded-2xl py-3 px-2 border transition flex flex-col items-center gap-1.5 text-sm ${active ? 'border-brand bg-brand/5' : 'border-black/5 hover:bg-black/5'}`}>
                      <div className={`w-9 h-9 rounded-full text-white flex items-center justify-center ${color}`}><Icon className="w-4 h-4" /></div>
                      <span className="font-medium">{t(labelKey)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="title" className="text-xs uppercase tracking-[0.2em] text-brand-dim">{t('create.title')}</Label>
              <Input id="title" data-testid="pin-title-input" value={title} onChange={e => setTitle(e.target.value)}
                placeholder={t('create.titlePlaceholder')} className="mt-2 rounded-xl bg-black/5 border-transparent focus:bg-white focus:border-brand" />
              {titleProfane && (
                <p data-testid="title-profanity-warning" className="mt-1.5 text-xs text-[#C86F53]">
                  {t('create.titleProfanity')}
                </p>
              )}
            </div>

            {category === 'yard_sale_DISABLED' && (
              <div>
                <Label htmlFor="price" className="text-xs uppercase tracking-[0.2em] text-brand-dim">Price (USD)</Label>
                <Input id="price" data-testid="pin-price-input" type="number" />
              </div>
            )}

            <div>
              <Label htmlFor="desc" className="text-xs uppercase tracking-[0.2em] text-brand-dim">{t('create.description')}</Label>
              <Textarea id="desc" data-testid="pin-description-input" value={description} onChange={e => setDescription(e.target.value)}
                placeholder={t('create.descPlaceholder')}
                className="mt-2 rounded-xl bg-black/5 border-transparent focus:bg-white focus:border-brand min-h-[80px]" />
              {descProfane && (
                <p data-testid="desc-profanity-warning" className="mt-1.5 text-xs text-[#C86F53]">
                  {t('create.descProfanity')}
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">{t('create.address')}</Label>
              <div className="mt-2 flex gap-2">
                <Input data-testid="pin-address-input" value={address} onChange={e => setAddress(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchAddress())}
                  placeholder={t('create.addressPlaceholder')} className="rounded-xl bg-black/5 border-transparent focus:bg-white focus:border-brand" />
                <button type="button" data-testid="address-search-btn" onClick={searchAddress} className="px-3 rounded-xl bg-brand-ink text-white text-sm flex items-center gap-1">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </button>
              </div>
              {addressResults.length > 0 && (
                <ul className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-black/5 bg-white scroll-soft" data-testid="address-results">
                  {addressResults.map((r, i) => (
                    <li key={r.label || `addr-${i}`}>
                      <button type="button" data-testid={`address-result-${i}`} onClick={() => pickAddress(r)} className="w-full text-left px-3 py-2 text-sm hover:bg-black/5 flex items-start gap-2">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 text-brand" /> <span className="text-brand-ink">{r.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 flex items-center justify-between rounded-xl bg-black/5 px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-brand-ink">{t('create.showAddress')}</div>
                  <div className="text-xs text-brand-dim">{t('create.showAddressHint')}</div>
                </div>
                <Switch data-testid="show-address-switch" checked={showAddress} onCheckedChange={setShowAddress} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">{t('create.whenActive')}</Label>
                <label className="flex items-center gap-2 text-xs text-brand-slate cursor-pointer">
                  <input data-testid="today-only-checkbox" type="checkbox" checked={todayOnly} onChange={e => setTodayOnly(e.target.checked)} />
                  {t('create.todayOnly')}
                </label>
              </div>
              {!todayOnly && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] text-brand-dim uppercase tracking-widest mb-1">{t('create.startDate')}</div>
                    <Input data-testid="start-date-input" type="date" value={startDate} min={todayStr()} max={addDaysStr(14)}
                      onChange={e => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value); }}
                      className="rounded-xl bg-black/5 border-transparent" />
                  </div>
                  <div>
                    <div className="text-[10px] text-brand-dim uppercase tracking-widest mb-1">{t('create.endDate')}</div>
                    <Input data-testid="end-date-input" type="date" value={endDate} min={startDate} max={addDaysStr(16)}
                      onChange={e => setEndDate(e.target.value)} className="rounded-xl bg-black/5 border-transparent" />
                  </div>
                </div>
              )}
              {category === 'yard_sale' && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] text-brand-dim uppercase tracking-widest mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {t('create.startTime')}</div>
                    <Input data-testid="start-time-input" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="rounded-xl bg-black/5 border-transparent" />
                  </div>
                  <div>
                    <div className="text-[10px] text-brand-dim uppercase tracking-widest mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {t('create.endTime')}</div>
                    <Input data-testid="end-time-input" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="rounded-xl bg-black/5 border-transparent" />
                  </div>
                </div>
              )}
              <div className="mt-2 text-[11px] text-brand-dim">{t('create.windowHint')}</div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">{t('create.photos', { count: maxPhotos })}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <div key={p.previewUrl || p.url || `photo-${i}`} className="relative w-20 h-20 rounded-xl overflow-hidden">
                    <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                {photos.length < maxPhotos && (
                  <label className="w-20 h-20 rounded-xl border-2 border-dashed border-black/15 flex items-center justify-center cursor-pointer hover:bg-black/5">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-brand-dim" />}
                    <input data-testid="photo-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={e => handleFiles(e.target.files)} />
                  </label>
                )}
              </div>
            </div>

            <div className="text-[11px] text-brand-dim">
              {t('create.pinLocation', { coords: latlng ? `${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)}` : '—' })}
            </div>
          </div>
          <div className="p-4 sm:p-5 border-t border-black/5 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">{t('common.cancel')}</Button>
            <Button onClick={submit} disabled={submitting || titleProfane || descProfane} className="rounded-full bg-brand hover:bg-brand-hover text-white px-6" data-testid="submit-pin-btn">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingId ? t('create.saveChanges') : t('create.dropPin')}
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
